import {Trans} from '@lingui/macro';
import {omit} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {getHasRuleFixUnrepliedAfterCondition} from '../../../../store/featureFlags/featureFlagsSelectors';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {UnrepliedAfterCondition} from '../../ruleConditionModel';
import {RuleConditionDefinition} from '../../ruleParameterModel';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {getRuleLabel} from '../ruleConditionProPill';
import {
  MultilineRuleParameterCardDefaultContentStyled,
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {renderGenericRuleParameters} from './ruleDefaultActionConditionCard';

/*
 * Types.
 */

interface RuleUnrepliedAfterConditionCardProps {
  condition: UnrepliedAfterCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  isSla?: boolean;
  label?: ReactNode;
  onUpdate: HandlerOf<UnrepliedAfterCondition>;
  onDelete?: HandlerOf<UnrepliedAfterCondition>;
}

/*
 * Styles.
 */

const SecondLineLabel = styled.div`
  white-space: nowrap;
`;

const SecondLineWrapper = styled.div`
  display: flex;
  align-items: center;
  > * {
    &:not(:last-child) {
      margin-right: 5px;
    }
  }
`;

/*
 * Constants.
 */

const NewActivityParamName = 'activity_type';

/*
 * Component.
 */

export const RuleUnrepliedAfterConditionCard: FC<RuleUnrepliedAfterConditionCardProps> = (props) => {
  const {
    condition,
    conditionDefinition,
    errorMessage,
    isDisabled,
    isSla = false,
    label: cardLabel,
    onUpdate,
    onDelete,
  } = props;
  const {namespace} = useBaseEditorContext();

  const hasRuleFixUnrepliedAfterCondition = useAppSelector(getHasRuleFixUnrepliedAfterCondition);

  const isInTeamNamespace = isTeamNamespace(namespace);

  // This should not happen, but safety check.
  const errorLabel = <Trans id="definition-condition-not-found">Condition {condition.type} not found</Trans>;
  if (!conditionDefinition) {
    return <RuleMultilineParameterCard icon={<RuleConditionIcon id={condition.type} />} title={errorLabel} />;
  }

  const hasNewActivityParameter = Boolean(
    conditionDefinition.parameters && conditionDefinition.parameters[NewActivityParamName],
  );

  if (!hasRuleFixUnrepliedAfterCondition || !hasNewActivityParameter || isSla) {
    const {
      parameters: [delayParameter, businessHoursParameter],
    } = renderGenericRuleParameters(
      condition,
      conditionDefinition,
      RuleDefaultCardTypesEnum.CONDITION,
      onUpdate,
      omit(conditionDefinition.parameters, NewActivityParamName),
      isDisabled,
    );

    const {label} = getRuleLabel(conditionDefinition.name);
    const title = (
      <>
        <MultilineRuleParameterCardDefaultTitleStyled>
          {cardLabel ?? label}
        </MultilineRuleParameterCardDefaultTitleStyled>
        {delayParameter}
      </>
    );

    // Business hours can only be selected in a team namespace.
    const businessHours = isInTeamNamespace ? (
      <MultilineRuleParameterCardDefaultContentStyled>
        {businessHoursParameter}
      </MultilineRuleParameterCardDefaultContentStyled>
    ) : null;

    return (
      <RuleMultilineParameterCard
        icon={<RuleConditionIcon id={condition.type} />}
        title={title}
        onCancel={onDelete ? () => onDelete(condition) : undefined}
      >
        {businessHours}
      </RuleMultilineParameterCard>
    );
  }

  const {
    parameters: [activityParameter, delayParameter, businessHoursParameter],
  } = renderGenericRuleParameters(
    condition,
    conditionDefinition,
    RuleDefaultCardTypesEnum.CONDITION,
    onUpdate,
    conditionDefinition.parameters,
    isDisabled,
  );

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="rule-unreplied-oldest">Oldest unreplied</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      {activityParameter}
    </>
  );

  return (
    <RuleMultilineParameterCard
      icon={<RuleConditionIcon id={condition.type} />}
      title={title}
      errorMessage={errorMessage}
      onCancel={onDelete ? () => onDelete(condition) : undefined}
    >
      <SecondLineWrapper>
        <SecondLineLabel>
          <Trans id="rule-unreplied-still-after">is still unreplied after</Trans>
        </SecondLineLabel>
        {delayParameter}
        {/* Business hours can only be selected in a team namespace. */}
        {isInTeamNamespace ? businessHoursParameter : null}
      </SecondLineWrapper>
    </RuleMultilineParameterCard>
  );
};
